// Disable no-re-export rule for entry point files
/* eslint-disable @atlaskit/editor/no-re-export */

import type { MessageDescriptor } from 'react-intl-next';

import {
	aiConfigItemMessages,
	aiLanguageMessages,
	aiMessages,
} from '@atlaskit/editor-common/ai-messages';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import {
	AIAdjustLengthIcon,
	AIBriefcaseIcon,
	AICasualIcon,
	AIHeartIcon,
	AIShortenIcon,
	AISpellcheckIcon,
	AIChangeToneIcon,
	AILengthenIcon,
} from '@atlaskit/editor-toolbar';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import type { NewCoreIconProps } from '@atlaskit/icon/types';
import { fg } from '@atlaskit/platform-feature-flags';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';

import type { AIPlugin } from '../../aiPluginType';
import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type {
	EditorPluginAIProvider,
	EditorPluginAIPublicOptions,
	SelectionToolbarDropdownConfig,
} from '../../types/types';

import type {
	EditorPluginAIConfigItem,
	EditorPluginAIConfigItemFactoryOptions,
	EditorPluginAIConfigItemWithOptions,
} from './config-items/config-items';
import { brainstorm } from './config-items/prompts/brainstorm/brainstorm';
import {
	changeToneToCasual,
	changeToneToCasualWithOptions,
	changeToneToEducational,
	changeToneToEducationalWithOptions,
	changeToneToEmpathetic,
	changeToneToEmpatheticWithOptions,
	changeToneToNeutral,
	changeToneToProfessional,
	changeToneToProfessionalWithOptions,
} from './config-items/prompts/change-tone/change-tone';
import { convertToBulletListWithOptions } from './config-items/prompts/convert-to-bullet-list/convert-to-bullet-list';
import { convertToTableWithOptions } from './config-items/prompts/convert-to-table/convert-to-table';
import {
	fixSpellingAndGrammar,
	fixSpellingAndGrammarWithOptions,
	improveWriting,
	improveWritingWithOptions,
} from './config-items/prompts/enhance/enhance';
import { getExperimentalPromptPlaceholderConfigItemWithOptions } from './config-items/prompts/experimental-prompt-placeholder/experimental-prompt-placeholder';
import { findActionItemsWithOptions } from './config-items/prompts/find-action-items/find-action-items';
import { freeGenerateAifc } from './config-items/prompts/free-generate-aifc/free-generate-aifc';
import { freeGenerate } from './config-items/prompts/free-generate/free-generate';
import { makeLonger, makeLongerWithOptions } from './config-items/prompts/make-longer/make-longer';
import {
	makeShorter,
	makeShorterWithOptions,
} from './config-items/prompts/make-shorter/make-shorter';
import { addPolish, addPolishWithOptions } from './config-items/prompts/polish/polish';
import {
	suggestTitleForConfluenceTitleToolbarWithOptions,
	suggestTitleWithOptions,
} from './config-items/prompts/suggest-title/suggest-title';
import {
	summarizeWriting,
	summarizeWritingWithOptions,
} from './config-items/prompts/summarize-writing/summarize-writing';
import {
	translationConfigItemWithOptions,
	translationConfigItemWithOptionsEnabledForSelectionOnly,
	createTranslationConfigItems,
} from './config-items/prompts/translate/translate';
import { getSortedSupportedTranslationLanguages } from './config-items/utils/translation-utils';
import { _getFetchCustomHeaders } from './utils';

// this is used in folder afm/confluence
export const UNSTABLE_configItems = {
	brainstormConfigItem: brainstorm(),
	freeGenerateAifcConfigItem: freeGenerateAifc(),
};

function getDynamicSurfaceConfigItems(
	opts: EditorPluginAIConfigItemFactoryOptions,
): EditorPluginAIConfigItemWithOptions[] {
	const dynamicSurfaceConfigItems: EditorPluginAIConfigItemWithOptions[] = [
		// Only brainstorm, suggest title and find action items are enabled for empty
		{
			config: improveWriting(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: fixSpellingAndGrammar(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: changeToneToProfessional(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: changeToneToEmpathetic(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: changeToneToCasual(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: changeToneToEducational(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: makeShorter(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		{
			config: makeLonger(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		addPolishWithOptions(opts),
		// Convert to table and bullet list are already on enabled for selection
		convertToBulletListWithOptions(opts),
		convertToTableWithOptions(opts),
		findActionItemsWithOptions(opts),
		suggestTitleForConfluenceTitleToolbarWithOptions(opts),
		{
			config: summarizeWriting(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		...translationConfigItemWithOptionsEnabledForSelectionOnly(opts),
	];

	// Fix this const name after team 25 with platform_editor_ai_aifc_streaming being cleaned up
	const dynamicSurfaceConfigItemsTeam25: EditorPluginAIConfigItemWithOptions[] = [
		{
			config: improveWriting(opts),
			triggers: { empty: { enabled: false }, selection: { enabled: true } },
		},
		addPolishWithOptions(opts),
		{
			config: fixSpellingAndGrammar(opts),
			triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
		},
		{
			config: changeToneToCasual(opts),
			triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
		},
		{
			config: changeToneToEmpathetic(opts),
			triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
		},
		{
			config: changeToneToProfessional(opts),
			triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
		},
		{
			config: makeShorter(opts),
			triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
		},
		{
			config: makeLonger(opts),
			triggers: { empty: { enabled: true, docMinSize: 1 }, selection: { enabled: true } },
		},
		...translationConfigItemWithOptionsEnabledForSelectionOnly(opts),
	];

	return opts.isInDocumentStreamingEnabled
		? dynamicSurfaceConfigItemsTeam25
		: dynamicSurfaceConfigItems;
}

/**
 * IMPORTANT: This API is likely to change in future versions when we look
 * to provide more configurability to consumers.
 * In future you may need to refactor to use a new API.
 *
 * ```ts
 * import { UNSTABLE_configItems, UNSTABLE_DO_NOT_USE_ONLY_FOR_CONFLUENCE_triggerConfigItem } from '@atlaskit/editor-plugin-ai/prebuilt/confluence';
 * // ...
 * UNSTABLE_DO_NOT_USE_ONLY_FOR_CONFLUENCE_triggerConfigItem({
 *   editorView,
 *   configItem: UNSTABLE_configItems.brainstormConfigItem,
 * })
 * ```
 */
export const UNSTABLE_DO_NOT_USE_ONLY_FOR_CONFLUENCE_triggerConfigItem = (props: {
	configItem: EditorPluginAIConfigItem;
	editorView: EditorView;
}): void => {
	const { editorView, configItem } = props;
	const openAIModalCommand = createOpenAIModalCommand({
		state: editorView.state,
		configItem,
		lastTriggeredFrom: 'triggerConfigItem',
		aiGlobalOptIn: {
			status: 'enabled',
			triggerOptInFlow: () => {},
		},
	});
	openAIModalCommand(editorView.state, editorView.dispatch);
};

// Page comment editor
export function createPageCommentEditorPluginAIOptions(
	options: EditorPluginAIPublicOptions,
): EditorPluginAIProvider {
	/**
	 * Not abstracting these defaults up, in order to be explicit about possible
	 * different behaviours between page comment and page editors.
	 */
	// Now deprecated - migrate to handleFeedbackSubmission
	const onExperienceEvent = options.onExperienceEvent || {
		THUMBS_UP: () => {},
		THUMBS_DOWN: () => {},
		REVIEW_STATE_ENTERED: () => {},
		EXPERIENCE_COMPLETE: () => {},
	};

	function getConfigItems(
		opts: EditorPluginAIConfigItemFactoryOptions,
	): EditorPluginAIConfigItemWithOptions[] {
		const list: EditorPluginAIConfigItemWithOptions[] = [
			changeToneToProfessionalWithOptions(opts),
			summarizeWritingWithOptions(opts),
			improveWritingWithOptions(opts),
			fixSpellingAndGrammarWithOptions(opts),
			makeShorterWithOptions(opts),
			makeLongerWithOptions(opts),
			convertToBulletListWithOptions(opts),
			convertToTableWithOptions(opts),
			suggestTitleWithOptions(opts),
			changeToneToEmpatheticWithOptions(opts),
			changeToneToCasualWithOptions(opts),
			changeToneToEducationalWithOptions(opts),
			...translationConfigItemWithOptions(opts),
		];

		const experimentalPromptPlaceholderData =
			getExperimentalPromptPlaceholderConfigItemWithOptions();
		if (experimentalPromptPlaceholderData) {
			const { listOrder, configItemWithOptions } = experimentalPromptPlaceholderData;
			list.splice(listOrder, 0, configItemWithOptions);
		}

		return list;
	}

	const product = options.product ? options.product : 'CONFLUENCE';

	const isInDocumentStreamingEnabled =
		options.isInDocumentStreamingEnabled ?? fg('platform_editor_ai_aifc_streaming');

	return {
		aiGlobalOptIn: options.aiGlobalOptIn,
		baseGenerate: freeGenerate(),
		configItemWithOptions: getConfigItems({
			product,
			isInDocumentStreamingEnabled,
		}),
		disableAISelectionToolbar: true,
		product,
		objectId: options.objectId,
		getFetchCustomHeaders: options.getFetchCustomHeaders || _getFetchCustomHeaders,
		onExperienceEvent,
		handleFeedbackSubmission: options.handleFeedbackSubmission,
		AIButtonWrapper: options.AIButtonWrapper,
		PromptEditor: options.PromptEditor,
		providers: options.providers,
		auditLogSettings: options.auditLogSettings,
		/**
		 * Below options are added to use rovo agent behind the scenes for free generate.
		 *
		 * Need to load rovo agents, so getChannelVariables and isRovoEnabled is set.
		 * showRovoAgentsInCommandPalette will disable showing Rovo agents section.
		 * useFreeGenerateEditorAgent will swap standard free generate prompt with
		 * 	rovo agent enabled free generate after agents are loaded.
		 * If loading agents fails, then fallback to standard free generate.
		 */
		getChannelVariables: options.getChannelVariables,
		isRovoEnabled: options.isRovoEnabled ?? true,
		showRovoAgentsInCommandPalette: options.showRovoAgentsInCommandPalette ?? false,
		useFreeGenerateEditorAgent: options.useFreeGenerateEditorAgent ?? true,
		onSubmit: options.onSubmit,
		onAnnotationsRemoved: options.onAnnotationsRemoved,
		getUserPreferences: options.getUserPreferences,
		isInDocumentStreamingEnabled,
		enableQuickPrompt: options.enableQuickPrompt ?? false,
	};
}

// Page editor
export const createPageEditorPluginAIOptions = (
	options: EditorPluginAIPublicOptions,
): EditorPluginAIProvider => {
	/**
	 * Not abstracting these defaults up, in order to be explicit about possible
	 * different behaviours between page comment and page editors.
	 */
	// Now deprecated - migrate to handleFeedbackSubmission
	const onExperienceEvent = options.onExperienceEvent || {
		THUMBS_UP: () => {},
		THUMBS_DOWN: () => {},
		REVIEW_STATE_ENTERED: () => {},
		EXPERIENCE_COMPLETE: () => {},
	};

	function getConfigItems(
		opts: EditorPluginAIConfigItemFactoryOptions,
	): EditorPluginAIConfigItemWithOptions[] {
		const list: EditorPluginAIConfigItemWithOptions[] = [
			changeToneToProfessionalWithOptions(opts),
			summarizeWritingWithOptions(opts),
			improveWritingWithOptions(opts),
			fixSpellingAndGrammarWithOptions(opts),
			makeShorterWithOptions(opts),
			makeLongerWithOptions(opts),
			convertToBulletListWithOptions(opts),
			convertToTableWithOptions(opts),
			findActionItemsWithOptions(opts),
			suggestTitleWithOptions(opts),
			changeToneToEmpatheticWithOptions(opts),
			changeToneToCasualWithOptions(opts),
			changeToneToEducationalWithOptions(opts),
			addPolishWithOptions(opts),
			...translationConfigItemWithOptions(opts),
		];

		const newListOrder: EditorPluginAIConfigItemWithOptions[] = [
			fixSpellingAndGrammarWithOptions(opts),
			changeToneToProfessionalWithOptions(opts),
			makeShorterWithOptions(opts),
			summarizeWritingWithOptions(opts),
			improveWritingWithOptions(opts),
			makeLongerWithOptions(opts),
			convertToBulletListWithOptions(opts),
			convertToTableWithOptions(opts),
			findActionItemsWithOptions(opts),
			suggestTitleWithOptions(opts),
			changeToneToEmpatheticWithOptions(opts),
			changeToneToCasualWithOptions(opts),
			changeToneToEducationalWithOptions(opts),
			addPolishWithOptions(opts),
			...translationConfigItemWithOptions(opts),
		];

		const experimentalPromptPlaceholderData =
			getExperimentalPromptPlaceholderConfigItemWithOptions();
		if (experimentalPromptPlaceholderData && !opts.isInDocumentStreamingEnabled) {
			const { listOrder, configItemWithOptions } = experimentalPromptPlaceholderData;
			list.splice(listOrder, 0, configItemWithOptions);
		}

		return opts.isInDocumentStreamingEnabled ? newListOrder : list;
	}

	const product = options.product ? options.product : 'CONFLUENCE';
	const isInDocumentStreamingEnabled =
		options.isInDocumentStreamingEnabled ?? fg('platform_editor_ai_aifc_streaming');
	const configItemOptions = {
		product,
		isInDocumentStreamingEnabled,
	};

	return {
		aiGlobalOptIn: options.aiGlobalOptIn,
		baseGenerate: freeGenerate(configItemOptions),
		configItemWithOptions: getConfigItems(configItemOptions),
		getSelectionToolbarDropdowns() {
			const selectionToolbarOptions: SelectionToolbarDropdownConfig[] = [
				{
					id: 'change-tone',
					label: aiMessages.confluenceChangeToneSelectionToolbarDropdownMenuTitle,
					options: [
						{
							label:
								aiConfigItemMessages.changeToneProfessionalToneSelectionToolbarDropdownItemTitle,
							configItem: changeToneToProfessional(configItemOptions),
						},
						{
							label: aiConfigItemMessages.changeToneEmpatheticToneSelectionToolbarDropdownItemTitle,
							configItem: changeToneToEmpathetic(configItemOptions),
						},
						{
							label: aiConfigItemMessages.changeToneCasualToneSelectionToolbarDropdownItemTitle,
							configItem: changeToneToCasual(configItemOptions),
						},
						{
							label: aiConfigItemMessages.changeToneNeutralToneSelectionToolbarDropdownItemTitle,
							configItem: changeToneToNeutral(configItemOptions),
						},
						{
							label:
								aiConfigItemMessages.changeToneEducationalToneSelectionToolbarDropdownItemTitle,
							configItem: changeToneToEducational(configItemOptions),
						},
					],
				},
				{
					id: 'rewrite',
					label: aiMessages.confluenceRewriteSelectionToolbarDropdownMenuTitle,
					options: [
						{
							label: aiConfigItemMessages.improveWritingSelectionToolbarDropdownItemTitle,
							configItem: improveWriting(configItemOptions),
						},
						{
							label: aiConfigItemMessages.fixSpellingGrammarSelectionToolbarDropdownItemTitle,
							configItem: fixSpellingAndGrammar(configItemOptions),
						},
						{
							label: aiConfigItemMessages.summarizeWritingSelectionToolbarDropdownItemTitle,
							configItem: summarizeWriting(configItemOptions),
						},
						{
							label: aiConfigItemMessages.makeShorterSelectionToolbarDropdownItemTitle,
							configItem: makeShorter(configItemOptions),
						},
					],
				},
			];

			const translationConfigItemsMap = createTranslationConfigItems(configItemOptions);
			const translateOptions = getSortedSupportedTranslationLanguages().map((language) => {
				const label = aiLanguageMessages[language];
				return {
					label,
					configItem: translationConfigItemsMap[language],
				};
			});

			selectionToolbarOptions.push({
				id: 'translate',
				label: aiMessages.confluenceTranslateSelectionToolbarDropdownMenuTitle,
				options: translateOptions,
			});
			return selectionToolbarOptions;
		},
		disableAISelectionToolbar: false,
		hidePrimaryToolbarButton: isInDocumentStreamingEnabled
			? false
			: options.hidePrimaryToolbarButton,
		product,
		objectId: options.objectId,
		getFetchCustomHeaders: options.getFetchCustomHeaders || _getFetchCustomHeaders,
		getChannelVariables: options.getChannelVariables,
		onDocChangeByAgent: options.onDocChangeByAgent,
		onAIProviderChanged: options.onAIProviderChanged,
		onExperienceEvent,
		handleFeedbackSubmission: options.handleFeedbackSubmission,
		AIButtonWrapper: options.AIButtonWrapper,
		PromptEditor: options.PromptEditor,
		disableInterrogation: options.disableInterrogation ?? false,
		aiUsageDisclaimer: options.aiUsageDisclaimer,
		isRovoEnabled: options.isRovoEnabled ?? true,
		/**
		 * Below options are added because;
		 * - Show rovo agents in command palette.
		 */
		showRovoAgentsInCommandPalette: options.showRovoAgentsInCommandPalette ?? true,
		providers: options.providers,
		auditLogSettings: options.auditLogSettings,
		aiSmartButtonOptions: options.aiSmartButtonOptions,
		heroConfigItem: isInDocumentStreamingEnabled
			? improveWritingWithOptions(configItemOptions)
			: undefined,
		...(options.aiGlobalOptIn.status === 'enabled' &&
			fg('ai-smart-button-team-25') && {
				dynamicSurfaceConfigItems: getDynamicSurfaceConfigItems(configItemOptions),
			}),
		onAnnotationsRemoved: options.onAnnotationsRemoved,
		getUserPreferences: options.getUserPreferences,
		onSubmit: options.onSubmit,
		isInDocumentStreamingEnabled,
		enableQuickPrompt: options.enableQuickPrompt ?? true,
	};
};

export type ObjectSidebarEditorAIActionSubItem = {
	icon: React.ComponentType<NewCoreIconProps>;
	onClick: () => void;
	title: MessageDescriptor;
};

export type ObjectSidebarEditorAIAction =
	| {
			icon: React.ComponentType<NewCoreIconProps>;
			onClick: () => void;
			title: MessageDescriptor;
	  }
	| {
			icon: React.ComponentType<NewCoreIconProps>;
			subItems: ObjectSidebarEditorAIActionSubItem[];
			title: MessageDescriptor;
	  };

/**
 * This function returns list of actions that will be shown in
 * 	staging full page editor object sidebar.
 * @param editorApi
 * @returns List of actions
 */
export const getEditorActionsForObjectSidebar = (
	editorApi: PublicPluginAPI<AIPlugin>,
): ObjectSidebarEditorAIAction[] => {
	const onActionClick = (configItem: EditorPluginAIConfigItem) => {
		return () => {
			editorApi.aiExperience?.actions.openAIModal({
				invokedFrom: 'objectSidebar',
				configItem,
			});
		};
	};

	const configItemOptions = {
		product: 'CONFLUENCE',
		isInDocumentStreamingEnabled: fg('platform_editor_ai_aifc_streaming'),
	};

	const changeToneActionItems = [
		{
			title: aiMessages.moreProfessionalToneToolbarDropdownIconTitle,
			icon: AIBriefcaseIcon,
			onClick: onActionClick(changeToneToProfessional(configItemOptions)),
		},
		{
			title: aiMessages.moreCasualToneToolbarDropdownIconTitle,
			icon: AICasualIcon,
			onClick: onActionClick(changeToneToCasual(configItemOptions)),
		},
		{
			title: aiMessages.moreEmpatheticToneToolbarDropdownIconTitle,
			icon: AIHeartIcon,
			onClick: onActionClick(changeToneToEmpathetic(configItemOptions)),
		},
	];
	const adjustLengthActionItems = [
		{
			title: aiMessages.makeShorterToolbarDropdownIconTitle,
			icon: AIShortenIcon,
			onClick: onActionClick(makeShorter(configItemOptions)),
		},
		{
			title: aiMessages.makeLongerToolbarDropdownIconTitle,
			icon: AILengthenIcon,
			onClick: onActionClick(makeLonger(configItemOptions)),
		},
	];

	const objectSideBarButtons = [
		{
			title: aiMessages.spellingAndGrammarToolbarDropdownIconTitle,
			icon: AISpellcheckIcon,
			onClick: onActionClick(fixSpellingAndGrammar(configItemOptions)),
		},
		{
			title: aiMessages.changeToneToolbarDropdownIconTitle,
			icon: AIChangeToneIcon,
			subItems: changeToneActionItems,
		},
		{
			title: aiMessages.adjustLengthToolbarDropdownIconTitle,
			icon: AIAdjustLengthIcon,
			subItems: adjustLengthActionItems,
		},
		{
			title: expValEquals('platform_editor_ai_rename_add_polish', 'isEnabled', true)
				? aiConfigItemMessages.improveFormattingTitle
				: aiConfigItemMessages.addPolishTitle,
			icon: AiGenerativeCleanupIcon,
			onClick: onActionClick(addPolish(configItemOptions)),
		},
	];
	return objectSideBarButtons;
};
